import { CampaignCreationChannelOptions } from 'modules/campaign-creation/components/CampaignCreationChannelOptions';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { Header } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { useNavigate } from 'react-router-dom';
import { AnchorText } from 'components/Text';
import { useCheckCampaignIsForAnalysis } from 'modules/campaign-creation/hooks/useCheckCampaignIsForAnalysis';
import { useSelectedCandidateBatchStatusesHandler } from 'modules/campaign-creation/hooks/useSelectedCandidateBatchStatusesHandler';
import { getTitle } from 'modules/campaign-creation/utils/getTitle';
import { getShouldCheckIfCandidatesAlreadyApplied } from 'modules/campaign-creation/utils/getShouldCheckIfCandidatesAlreadyApplied';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { CampaignType } from 'generated-graphql/types';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

type CampaignCreationChannelStepHeaderProps = {
  onBack: () => void;
};

const CampaignCreationChannelStepHeader: React.FC<
  CampaignCreationChannelStepHeaderProps
> = ({ onBack }) => {
  const navigate = useNavigate();
  const state = useCampaignCreationStore((store) => store.state);
  return (
    <Header
      title="Choose channel"
      subtitle={
        <AnchorText
          onClick={() => {
            navigate(Paths.CHANNELS);
          }}
        >
          Set up and manage channels
        </AnchorText>
      }
      currentBreadcrumb={getTitle(state)}
      breadcrumbs={[{ name: 'Campaigns', path: Paths.CAMPAIGNS }]}
      onBack={() => {
        onBack();
      }}
    />
  );
};

export const CampaignCreationChannelStep: React.FC = () => {
  const { isCampaignForAnalysis } = useCheckCampaignIsForAnalysis();
  const campaignType = useCampaignCreationStore(
    (store) => store.state?.campaignType
  );
  const { candidateBatchStatuses } = useSelectedCandidateBatchStatusesHandler();
  const onChannelBack = useCampaignCreationStore(
    (store) => store.onChannelBack
  );
  const ATSJobDescriptionState = useCampaignCreationStore(
    (store) => store.state?.ATSJobDescriptionState
  );
  const channel = useCampaignCreationStore((store) => store.state?.channel);
  const state = useCampaignCreationStore((store) => store.state);
  const csvColumns = useCampaignCreationStore(
    (store) => store.state?.CSVImportState?.csvColumns
  );
  const onChannelSubmit = useCampaignCreationStore(
    (store) => store.onChannelSubmit
  );
  const organizationConfig = useOrganizationConfig();
  const allowWhatsAppOnColdOutreach = !!useFlag(
    Flags.ALLOW_WHATSAPP_ON_COLD_OUTREACH
  );

  const shouldCheckIfCandidatesAlreadyApplied =
    getShouldCheckIfCandidatesAlreadyApplied(
      campaignType,
      isCampaignForAnalysis,
      !!ATSJobDescriptionState?.selectedJob?.atsJobId
    );

  // WhatsApp should be disabled for cold outreach to prevent account blocking
  // - NewCandidateOutreach (from CANDIDATE_APPLICATION) hides WhatsApp, but NOT for
  //   ATS list imports which skip that step and are handled by the org config instead
  // - ATS list imports hide WhatsApp unless the org has explicitly enabled it
  // - The ALLOW_WHATSAPP_ON_COLD_OUTREACH flag overrides both gates per-org
  const isColdOutreach = !!(
    (state?.campaignType === CampaignType.NewCandidateOutreach &&
      !state?.ATSImportState?.atsListId) ||
    (state?.ATSImportState?.atsListId &&
      !organizationConfig?.allowWhatsAppForAtsListCampaigns)
  );

  // ATS list imports skip CANDIDATE_APPLICATION, so back should not go there
  const isAtsListImport = !!state?.ATSImportState?.atsListId;

  const handleBack = () => {
    onChannelBack({
      selectedCandidateBatchStatuses: candidateBatchStatuses,
      shouldGoToCandidateApplication:
        shouldCheckIfCandidatesAlreadyApplied && !isAtsListImport
    });
  };

  return (
    <>
      <CampaignCreationChannelStepHeader onBack={handleBack} />
      <CampaignCreationChannelOptions
        isColdOutreach={isColdOutreach}
        allowWhatsAppOnColdOutreach={allowWhatsAppOnColdOutreach}
        defaultChannel={channel || null}
        defaultEmailFallbackEnabled={state?.emailChannelFallbackEnabled ?? null}
        csvColumns={csvColumns || null}
        onSubmit={(values) => {
          onChannelSubmit({
            channel: values.channel,
            emailChannelFallbackEnabled:
              values.emailChannelFallbackEnabled ?? null
          });
        }}
      />
    </>
  );
};
